import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  TextArea,
  Confirm,
  Dropdown,
} from "semantic-ui-react";
import { useFormik } from "formik";
import {
  buildInitialValues,
  validationSchema,
} from "./RutasDidacticasForm.form";
import { DidacticRoutesAPI } from "../../../../api";

const didacticRoutesAPI = new DidacticRoutesAPI();

export function RutasDidacticasForm({
  accessToken,
  classNumber,
  teacher,
  scene,
  onClose,
  onReload,
  forceCreateMode, // Nuevo parámetro
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // Rutas del teacher (para dropdown) si ya existen
  const [routesOptions, setRoutesOptions] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  //console.log("Teacher.rutasDidacticas", teacher);

  // Verifica si teacher.rutasDidacticas está vacío
  const hasRoutes =
  !forceCreateMode && // Ignorar rutas existentes si está forzado
  teacher.rutasDidacticas &&
  teacher.rutasDidacticas.length > 0;

  useEffect(() => {
    if (hasRoutes) {
      fetchTeacherRoutes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRoutes]);

  const fetchTeacherRoutes = async () => {
    try {
      const result = await didacticRoutesAPI.getRoutesByTeacher(
        accessToken,
        teacher._id
      );
      // result.routes => array con { _id, nameRute, ... }
      const options = (result.routes || []).map((rt) => ({
        key: rt._id,
        text: rt.nameRute,
        value: rt._id,
      }));
      setRoutesOptions(options);
    } catch (error) {
      console.error("Error al obtener itinerarios del profesor:", error);
    }
  };

  // Formik (solo se usa para crear nueva ruta)
  const formik = useFormik({
    initialValues: buildInitialValues(classNumber),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: () => {
      // Muestra confirm
      setShowConfirm(true);
    },
  });

  /**
   * Se llama cuando el usuario confirma en el Confirm
   */
  const handleConfirm = async () => {
    setShowConfirm(false);
    setLoading(true);

    try {
      if (!hasRoutes) {
        // 1) CREAR RUTA NUEVA
        const data = {
          nameRute: formik.values.nameRute,
          description: formik.values.description,
          classnumber: formik.values.classnumber,
          institution_Id: teacher.institution,
        };

        const result = await didacticRoutesAPI.createDidacticRoute(
          accessToken,
          teacher._id,
          teacher.institution._id,
          data
        );
        console.log("Nueva ruta creada =>", result.route);

        // Si quisieras, podrías luego hacer un PATCH con la scene.
        // O recargar la lista de rutas, etc.

      } else {
        // 2) AGREGAR ESCENA A ITINERARIO EXISTENTE
        if (!selectedRouteId) {
          throw new Error("Debes seleccionar un Itinerario antes de continuar");
        }

        // 2.1) Obtener la ruta para ver su scenesRute actual
        const routeDetail = await didacticRoutesAPI.getRouteById(
          accessToken,
          selectedRouteId
        );
        // routeDetail.route.scenesRute => un array de objetos, ej:
        // [ { scene: "66ff9e6119a925e2ab2eaeed" }, ... ]

        // 2.2) Convirtiendo a array de strings
        const existingStrings = routeDetail.route.scenesRute.map((item) => item.scene);

        // 2.3) Revisar si ya existe la scene.refId
        if (!existingStrings.includes(scene.refId)) {
          existingStrings.push(scene.refId); // push el refId como string
        }

        // 2.4) Enviar un PATCH con scenesRute = array de strings
        const patchData = { scenesRute: existingStrings };

        await didacticRoutesAPI.updateDidacticRoute(
          accessToken,
          selectedRouteId,
          patchData
        );
      
      }

      onClose();
      onReload();
      window.location.reload();
    } catch (error) {
      console.error("Error al crear/actualizar el Itinerario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  return (
    <Modal open={true} onClose={onClose} size="tiny">
      <Modal.Header>
        {hasRoutes && !forceCreateMode
          ? "Agregar Escena a Itinerario"
          : "Crear Nuevo Itinerario"}
      </Modal.Header>

      <Modal.Content>
        {!hasRoutes || forceCreateMode ?  (
          // Form para crear la ruta
          <Form onSubmit={formik.handleSubmit}>
            <Form.Input
              label="Nombre del Itinerario"
              name="nameRute"
              value={formik.values.nameRute}
              onChange={formik.handleChange}
              error={
                formik.errors.nameRute
                  ? { content: formik.errors.nameRute, pointing: "above" }
                  : null
              }
            />

            <Form.Field
              control={TextArea}
              label="Descripción del Itinerario"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              error={
                formik.errors.description
                  ? { content: formik.errors.description, pointing: "above" }
                  : null
              }
            />

            <Form.Input
              label="Nombre del Aula, Clase o Módulo"
              name="classnumber"
              value={formik.values.classnumber}
              onChange={formik.handleChange}
              error={
                formik.errors.classnumber
                  ? { content: formik.errors.classnumber, pointing: "above" }
                  : null
              }
            />

            {!formik.values.classnumber && (
              <p style={{ color: "red" }}>
                Debes seleccionar una clase o modulo donde impartirás el Itinerario.
              </p>
            )}

            {/* 
              IMPORTANTE: Botón de tipo "submit" para que Formik lo interprete
              como envío natural del formulario. 
            */}
            <Button type="submit" primary loading={loading}>
              Crear Itinerario
            </Button>
          </Form>
        ) : (
          <>
            <p>
              Selecciona el Itinerario al que vas a añadir la Escena:{" "}
              <strong>{scene?.title}</strong>
            </p>
            <Dropdown
              placeholder="Selecciona una Escena"
              fluid
              selection
              options={routesOptions}
              value={selectedRouteId}
              onChange={(_, { value }) => setSelectedRouteId(value)}
            />
            <div style={{ marginTop: "10px" }}> </div>
            {/* 
              Botón *fuera* del <Form> => type="button"
              para no provocar submit de Formik.
            */}
            <Button
              type="button"
              primary
              loading={loading}
              onClick={() => {
                if (!selectedRouteId) {
                  alert("Debes seleccionar un Itinerario antes de continuar");
                  return;
                }
                setShowConfirm(true);
              }}
            >
              Agregar Escena
            </Button>
          </>
        )}
      </Modal.Content>

      {/* Confirm de Semantic UI */}
      <Confirm
        open={showConfirm}
        onCancel={handleCancelConfirm}
        onConfirm={handleConfirm}
        content={
          hasRoutes
            ? `¿Estás seguro de que quieres agregar la Escena "${scene?.title}" al itinerario seleccionado?`
            : `¿Estás seguro de que quieres crear un nuev Itinerario didáctico con el nombre "${formik.values.nameRute}"?`
        }
        cancelButton="No"
        confirmButton="Sí, continuar"
      />
    </Modal>
  );
}
